import { FormGroup } from '@angular/forms';
import { CalculationContext, FieldKey, SetOptions } from '@marketiq/calc-core';
import { ColumnIdMap } from './column-id-map';

/**
 * Bridges a reactive {@link FormGroup} to the calc-core {@link CalculationContext}, so the pure
 * rules can read/write form fields by symbolic {@link FieldKey} without ever importing `@angular/forms`.
 * This is the seam (plan §2.7) that makes the math testable in isolation and reusable server-side.
 *
 *   ctx.get(key)        -> formGroup.get(map.idFor(key)).value
 *   ctx.set(key, v, o)  -> formGroup.get(map.idFor(key)).setValue(v, o)
 */
export class FormGroupCalculationContext implements CalculationContext {
  /**
   * @param defaultSetOptions applied to every `set` unless the call overrides them. The listener
   *   engine passes `{ emitEvent: false }` so writing a rule's output doesn't re-trigger listeners
   *   (the loop-prevention the legacy listeners do with `patchValue(..., { emitEvent: false })`).
   */
  constructor(
    private readonly fg: FormGroup,
    private readonly columnIdMap: ColumnIdMap,
    private readonly defaultSetOptions?: SetOptions
  ) {}

  get<T = unknown>(key: FieldKey): T | null | undefined {
    return this.control(key)?.value as T | null | undefined;
  }

  set<T = unknown>(key: FieldKey, value: T, opts?: SetOptions): void {
    const ctrl = this.control(key);
    if (!ctrl) return;
    ctrl.setValue(value, { ...this.defaultSetOptions, ...opts });
    ctrl.markAsDirty();
    ctrl.markAsTouched();
  }

  has(key: FieldKey): boolean {
    return !!this.control(key);
  }

  *watch(key: FieldKey): Iterable<unknown> {
    const ctrl = this.control(key);
    if (ctrl) {
      yield ctrl.value;
    }
  }

  private control(key: FieldKey) {
    const id = this.columnIdMap.idFor(key);
    return id == null ? null : this.fg.get(String(id));
  }
}
